/**
 * Sound Utilities
 * Generate notification sounds using Web Audio API
 */

type SoundType = 'ding' | 'chime' | 'urgent';

class SoundGenerator {
  private audioContext: AudioContext | null = null;

  constructor() {
    // Initialize AudioContext lazily (requires user interaction)
    if (typeof window !== 'undefined' && 'AudioContext' in window) {
      this.audioContext = new AudioContext();
    }
  }

  /**
   * Play a notification sound
   */
  async play(type: SoundType): Promise<void> {
    if (!this.audioContext) {
      console.warn('AudioContext not available');
      return;
    }

    // Resume audio context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    switch (type) {
      case 'ding':
        this.playDing();
        break;
      case 'chime':
        this.playChime();
        break;
      case 'urgent':
        this.playUrgent();
        break;
      default:
        console.warn(`Unknown sound type: ${type}`);
    }
  }

  /**
   * DING - Single tone (NORMAL priority)
   * Pleasant, subtle notification sound
   */
  private playDing(): void {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Frequency: 800 Hz (pleasant mid-high tone)
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    // Volume envelope (fade in/out)
    const now = this.audioContext.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01); // Quick fade in
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3); // Fade out

    oscillator.start(now);
    oscillator.stop(now + 0.3);
  }

  /**
   * CHIME - Two-tone (HIGH priority)
   * More noticeable, requires attention
   */
  private playChime(): void {
    if (!this.audioContext) return;

    const playTone = (frequency: number, startTime: number, duration: number) => {
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext!.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.4, startTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };

    const now = this.audioContext.currentTime;

    // First tone: 600 Hz
    playTone(600, now, 0.15);

    // Second tone: 800 Hz (slightly higher)
    playTone(800, now + 0.15, 0.2);
  }

  /**
   * URGENT - Repeating alert (URGENT priority)
   * Demanding attention, impossible to ignore
   */
  private playUrgent(): void {
    if (!this.audioContext) return;

    const playBeep = (startTime: number) => {
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext!.destination);

      // Alternating frequencies for more urgency
      oscillator.frequency.setValueAtTime(900, startTime);
      oscillator.frequency.setValueAtTime(700, startTime + 0.1);
      oscillator.type = 'square'; // Harsher sound for urgency

      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.5, startTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, startTime + 0.15);

      oscillator.start(startTime);
      oscillator.stop(startTime + 0.15);
    };

    const now = this.audioContext.currentTime;

    // Three rapid beeps
    playBeep(now);
    playBeep(now + 0.2);
    playBeep(now + 0.4);
  }

  /**
   * Pre-initialize audio context on user interaction
   * Call this on first user click/tap to avoid autoplay restrictions
   */
  async initialize(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }
}

// Singleton instance
const soundGenerator = new SoundGenerator();

/**
 * Play a notification sound
 * @param type - Type of sound to play
 */
export const playNotificationSound = async (type: SoundType): Promise<void> => {
  try {
    await soundGenerator.play(type);
  } catch (error) {
    console.error('Failed to play notification sound:', error);
  }
};

/**
 * Initialize sound system (call on first user interaction)
 */
export const initializeSound = async (): Promise<void> => {
  try {
    await soundGenerator.initialize();
  } catch (error) {
    console.error('Failed to initialize sound system:', error);
  }
};

/**
 * Map filename to sound type for backward compatibility
 */
export const playSoundByFilename = async (filename: string): Promise<void> => {
  const soundMap: Record<string, SoundType> = {
    'ding.mp3': 'ding',
    'chime.mp3': 'chime',
    'urgent.mp3': 'urgent',
  };

  const type = soundMap[filename];
  if (type) {
    await playNotificationSound(type);
  } else {
    console.warn(`Unknown sound file: ${filename}`);
  }
};
